import { errorFrom } from "@/lib/errors";
import { useTranslation } from "react-i18next";
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Film, ArrowUpRight, Loader2, Clapperboard, Layers, Trash2 } from "lucide-react";
import { Layout } from "@/components/hud/Layout";
import { Button } from "@/components/primitives/Button";
import { StatusPill } from "@/components/primitives/StatusPill";
import { listProjects, legacySummary, claimLegacy, deleteProject, type ProjectSummary } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { projectStatusMeta } from "@/lib/status";
import { DASHBOARD } from "@/constants/testIds";
import { cn } from "@/lib/utils";
import { directProjectPath, isDirectProject } from "@/lib/directApi";

function ProjectPoster({ aspect, seed }: { aspect: "9:16" | "16:9"; seed: number }) {
  const hue = 210 + (seed % 5) * 6;
  return (
    <div className="relative aspect-[16/10] overflow-hidden rounded-t-2xl bg-[#060A14]">
      <div
        aria-hidden
        className="absolute inset-0 transition-transform duration-700 ease-out group-hover:scale-105"
        style={{
          background: `radial-gradient(70% 60% at 50% 115%, hsla(${hue},100%,60%,0.32), transparent 60%), radial-gradient(60% 50% at 20% 0%, rgba(15,182,214,0.16), transparent 60%), linear-gradient(180deg, #0B1428 0%, #070C18 100%)`,
        }}
      />
      <div
        aria-hidden
        className="absolute inset-0 opacity-[0.10] [background-image:linear-gradient(rgba(46,123,255,0.9)_1px,transparent_1px),linear-gradient(90deg,rgba(46,123,255,0.9)_1px,transparent_1px)] [background-size:26px_26px] [mask-image:radial-gradient(80%_80%_at_50%_50%,black,transparent)]"
      />
      <div aria-hidden className="absolute inset-x-0 top-[62%] h-px bg-gradient-to-r from-transparent via-[#7FD4FF]/60 to-transparent" />
      <div className="relative flex h-full items-center justify-center">
        <div
          className={cn(
            "relative flex items-center justify-center rounded-md border border-[#2E7BFF]/60 bg-[#05070D]/75 shadow-[0_0_34px_-8px_rgba(46,123,255,0.7),inset_0_0_30px_-16px_rgba(46,123,255,0.8)] transition-[transform,box-shadow] duration-500 ease-out group-hover:-translate-y-1 group-hover:shadow-[0_0_44px_-6px_rgba(46,123,255,0.9),inset_0_0_30px_-16px_rgba(46,123,255,0.8)]",
            aspect === "9:16" ? "h-[70%] aspect-[9/16]" : "w-[64%] aspect-video",
          )}
        >
          <span aria-hidden className="absolute left-1.5 top-1.5 h-2.5 w-2.5 border-l border-t border-[#7FD4FF]/80" />
          <span aria-hidden className="absolute right-1.5 top-1.5 h-2.5 w-2.5 border-r border-t border-[#7FD4FF]/80" />
          <span aria-hidden className="absolute bottom-1.5 left-1.5 h-2.5 w-2.5 border-b border-l border-[#7FD4FF]/80" />
          <span aria-hidden className="absolute bottom-1.5 right-1.5 h-2.5 w-2.5 border-b border-r border-[#7FD4FF]/80" />
          <img src="/brand/logo-simulation-studios.jpeg" alt="" className="logo-neon h-10 w-10 object-contain" />
        </div>
      </div>
      <span className="absolute left-3 top-3 rounded-md border border-[#2E7BFF]/40 bg-[#05070D]/85 px-2 py-0.5 font-display text-[11px] font-bold tracking-[0.16em] text-[#7FD4FF] shadow-[0_0_14px_-4px_rgba(46,123,255,0.8)]">
        <span data-testid="project-card-aspect">{aspect}</span>
      </span>
      <div aria-hidden className="absolute inset-x-0 bottom-0 h-12 bg-gradient-to-t from-[#0C1220] to-transparent" />
    </div>
  );
}

export default function Dashboard() {
  const { t } = useTranslation();
  const [projects, setProjects] = useState<ProjectSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();
  const navigate = useNavigate();
  const [legacyTotal, setLegacyTotal] = useState<number | null>(null);
  const [claiming, setClaiming] = useState(false);
  const [deleting, setDeleting] = useState<ProjectSummary | null>(null);
  const [deleteBusy, setDeleteBusy] = useState(false);

  // Same backend action as the project detail page (DELETE /projects/{id}, owner-scoped cascade).
  const removeProject = async () => {
    if (!deleting || deleteBusy) return;
    setDeleteBusy(true);
    try {
      await deleteProject(deleting.id);
      setProjects((prev) => prev.filter((p) => p.id !== deleting.id));
      toast.success(t("dashboard.projectDeleted"));
      setDeleting(null);
    } catch (e: any) {
      toast.error(errorFrom(e, "dashboard.deleteFailed"));
    } finally {
      setDeleteBusy(false);
    }
  };

  useEffect(() => {
    if (user?.role !== "admin") return;
    legacySummary()
      .then((d) => setLegacyTotal(d.total))
      .catch(() => setLegacyTotal(null));
  }, [user]);

  const doClaimLegacy = async () => {
    if (
      !window.confirm(
        t("dashboard.legacyConfirm"),
      )
    )
      return;
    setClaiming(true);
    try {
      const res = await claimLegacy();
      toast.success(`Dati legacy rivendicati: ${res.total} documenti`);
      setLegacyTotal(0);
      load();
    } catch {
      toast.error(t("dashboard.legacyFailed"));
    } finally {
      setClaiming(false);
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      setProjects(await listProjects());
    } catch {
      toast.error(t("dashboard.loadFailed"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const createBtn = (testId: string) => (
    <button type="button" data-testid={testId} onClick={() => navigate("/create")} className="cta-yellow h-12 px-7 text-sm sm:h-13 sm:px-9 sm:text-base">
      <span className="inline-flex items-center gap-2.5">
        <Plus className="h-5 w-5" /> {t("dashboard.newProject")}
      </span>
    </button>
  );

  const total = projects.length;
  const scenes = projects.reduce((n, p) => n + p.scene_count, 0);

  return (
    <Layout>
      <main data-testid={DASHBOARD.page} className="relative mx-auto max-w-7xl px-6 pb-32 pt-14 sm:px-10">
        <div aria-hidden className="pointer-events-none absolute -top-32 right-0 h-[460px] w-[700px] rounded-full bg-[#2E7BFF]/[0.10] blur-[140px]" />

        <header className="relative flex flex-col gap-8 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <span className="inline-flex items-center gap-3 font-display text-xs font-semibold uppercase tracking-[0.34em] text-[#7FD4FF]">
              <span className="h-px w-8 bg-gradient-to-r from-[#2E7BFF] to-transparent" />
              {t("dashboard.eyebrow")}
            </span>
            <h1 className="mt-4 font-display text-4xl font-bold uppercase leading-[0.98] tracking-wide text-foreground sm:text-5xl lg:text-6xl">
              {t("dashboard.titleA")} <span className="text-neon">{t("dashboard.titleB")}</span>
            </h1>
            {!loading && total > 0 && (
              <div className="mt-5 flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
                <span className="glass inline-flex items-center gap-2 rounded-full px-3 py-1.5 !shadow-none">
                  <Layers className="h-3.5 w-3.5 text-[#7FD4FF]" strokeWidth={1.75} />
                  {t("dashboard.projectsCount", { count: total })}
                </span>
                <span className="glass inline-flex items-center gap-2 rounded-full px-3 py-1.5 !shadow-none">
                  <Film className="h-3.5 w-3.5 text-[#7FD4FF]" strokeWidth={1.75} />
                  {t("dashboard.scenesCount", { count: scenes })}
                </span>
              </div>
            )}
          </div>
          {!loading && total > 0 && createBtn(DASHBOARD.newProjectBtn)}
        </header>

        {user?.role === "admin" && !!legacyTotal && legacyTotal > 0 && (
          <div data-testid="legacy-claim-panel" className="glass mt-10 flex flex-wrap items-center justify-between gap-4 rounded-xl px-5 py-4 !shadow-none">
            <p className="text-sm text-muted-foreground">
              <span className="font-display font-semibold uppercase tracking-wide text-foreground">{t("dashboard.legacyTitle")}</span>
              {" · "}{t("dashboard.legacyBody", { count: legacyTotal })}
            </p>
            <Button variant="yellow" size="sm" onClick={doClaimLegacy} disabled={claiming} data-testid="legacy-claim-btn">
              {claiming ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              {t("dashboard.legacyClaim")}
            </Button>
          </div>
        )}

        <section className="relative mt-14">
          {loading ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" /> Caricamento…
            </div>
          ) : total === 0 ? (
            <div data-testid={DASHBOARD.emptyState} className="glass brackets relative overflow-hidden rounded-3xl px-8 py-24 text-center">
              <span className="bk" />
              <div aria-hidden className="absolute inset-0 bg-[radial-gradient(60%_60%_at_50%_100%,rgba(46,123,255,0.22),transparent_65%)]" />
              <div
                aria-hidden
                className="absolute inset-0 opacity-[0.08] [background-image:linear-gradient(rgba(46,123,255,0.9)_1px,transparent_1px),linear-gradient(90deg,rgba(46,123,255,0.9)_1px,transparent_1px)] [background-size:36px_36px] [mask-image:radial-gradient(60%_70%_at_50%_60%,black,transparent)]"
              />
              <div className="relative">
                <div className="mx-auto grid h-20 w-20 place-items-center rounded-2xl border border-[#2E7BFF]/50 bg-[#05070D] shadow-[0_0_40px_-8px_rgba(46,123,255,0.8)]">
                  <Clapperboard className="h-8 w-8 text-[#7FD4FF]" strokeWidth={1.5} />
                </div>
                <p className="mt-8 font-display text-3xl font-bold uppercase tracking-wide text-foreground sm:text-4xl">
                  {t("dashboard.emptyTitleA")} <span className="text-neon">{t("dashboard.emptyTitleB")}</span>
                </p>
                <p className="mx-auto mt-3 max-w-md text-sm text-muted-foreground">
                  {t("dashboard.emptyBody")}
                </p>
                <div className="mt-10 flex justify-center">{createBtn(DASHBOARD.newProjectBtn)}</div>
              </div>
            </div>
          ) : (
            <div className="grid gap-7 sm:grid-cols-2 lg:grid-cols-3">
              {projects.map((p, i) => {
                const meta = projectStatusMeta(p.status);
                const aspect = p.aspect_ratio || "9:16";
                return (
                  <Link
                    key={p.id}
                    to={isDirectProject(p) ? directProjectPath(p.id) : `/projects/${p.id}/scenes`}
                    data-testid={DASHBOARD.projectCard}
                    style={{ animationDelay: `${Math.min(i, 8) * 70}ms` }}
                    className="group relative block animate-in fade-in slide-in-from-bottom-3 rounded-2xl border border-[#2E7BFF]/20 bg-[#0C1220] fill-mode-both duration-500 transition-[transform,border-color,box-shadow] hover:-translate-y-1.5 hover:border-[#2E7BFF]/70 hover:shadow-[0_30px_70px_-30px_rgba(0,0,0,0.95),0_0_50px_-14px_rgba(46,123,255,0.65)]"
                  >
                    <div aria-hidden className="pointer-events-none absolute inset-x-8 top-0 h-px bg-gradient-to-r from-transparent via-[#7FD4FF]/60 to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
                    <button
                      type="button"
                      data-testid="project-card-delete-btn"
                      aria-label={t("dashboard.deleteProject")}
                      title={t("dashboard.deleteProject")}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setDeleting(p);
                      }}
                      className="nav-icon absolute right-3 top-3 z-10 !h-8 !w-8 opacity-70 transition-[opacity,color,border-color,box-shadow] hover:!border-[#F28B82]/60 hover:!text-[#F28B82] hover:opacity-100 hover:!shadow-[0_0_18px_-4px_rgba(242,139,130,0.6)]"
                    >
                      <Trash2 className="h-3.5 w-3.5" strokeWidth={1.75} />
                    </button>
                    <ProjectPoster aspect={aspect} seed={i} />
                    <div className="p-5 pt-4">
                      <div className="flex items-start justify-between gap-3">
                        <h3 className="line-clamp-2 font-display text-lg font-bold uppercase leading-snug tracking-wide text-foreground">
                          {p.title}
                        </h3>
                        <span className="nav-icon !h-8 !w-8 shrink-0 opacity-0 transition-opacity duration-300 group-hover:opacity-100">
                          <ArrowUpRight className="h-4 w-4 text-[#7FD4FF]" />
                        </span>
                      </div>
                      <div className="mt-4 flex items-center justify-between gap-3 text-xs text-muted-foreground">
                        <span className="flex items-center gap-1.5">
                          <Film className="h-3.5 w-3.5 text-[#7FD4FF]" strokeWidth={1.75} />
                          {isDirectProject(p) ? t("direct.modeMulti", { n: p.segment_count ?? 0 }) : t("dashboard.scenesCount", { count: p.scene_count })}
                        </span>
                        <StatusPill tone={meta.tone}>{meta.label}</StatusPill>
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          )}
        </section>
      </main>

      {deleting && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={() => !deleteBusy && setDeleting(null)}>
          <div className="glass-panel w-full max-w-md rounded-2xl p-6" onClick={(e) => e.stopPropagation()} data-testid="project-delete-confirm">
            <h3 className="font-display text-base font-semibold uppercase tracking-wide text-foreground">{t("dashboard.deleteConfirmTitle")}</h3>
            <p className="mt-2 text-sm text-muted-foreground">{t("dashboard.deleteConfirmBody")}</p>
            <p className="mt-2 line-clamp-1 text-xs text-[#9CC8FF]">{deleting.title}</p>
            <div className="mt-5 flex justify-end gap-2">
              <Button variant="ghost" size="sm" disabled={deleteBusy} onClick={() => setDeleting(null)} data-testid="project-delete-cancel">
                {t("common.cancel")}
              </Button>
              <Button size="sm" disabled={deleteBusy} onClick={removeProject} data-testid="project-delete-accept">
                {deleteBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />} {t("common.delete")}
              </Button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}
